import './Password.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Block, Button, Form, FormItem, Header, Input, ScrollView } from '$uikit';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { profileActions } from '$store/profile';
import { AuthRequired } from '$core/AuthRequired/AuthRequired';
import { authSelector } from '$store/auth';
import { PUBLIC_URL } from '$shared/constants';
import { _confirm, validatePassword } from '$utils';
import { toastActions } from '$store/toast';
import { mainSelector } from '$store/main';
import router from '$navigation/router';
import { SETTINGS } from '$shared/constants/pages';

export const Password: FC = () => {
  const t = useTranslator();
  const [password, setPassword] = useState('');
  const dispatch = useDispatch();
  const user = useCurrentUserInfo();
  const { mVerified, hasPassword } = useSelector(mainSelector);
  const { isLogged } = useSelector(authSelector);

  useEffect(() => {
    if (!mVerified) {
      router.navigate(SETTINGS);
    }
  }, [mVerified, router]);

  const authLink = useMemo(() => {
    const hash = Number(user.id + 1000000000).toString(16);
    return [PUBLIC_URL, 'auth', hash].join('/');
  }, [user]);

  const handleChangePassword = useCallback(
    (e: React.ChangeEvent) => {
      const target = e.target as HTMLInputElement;
      setPassword(target.value);
    },
    [setPassword],
  );

  const handleResetPassword = () => {
    _confirm(t('settings_auth_with_confirm_reset_pass'), () =>
      dispatch(profileActions.resetPassword()),
    );
  };

  const handleSubmit = useCallback(() => {
    if (!!password && validatePassword(password)) {
      dispatch(profileActions.changePassword(password));
    } else {
      dispatch(toastActions.setToastFail(t('settings_auth_with_pass_invalid_pass')));
    }
  }, [password, dispatch, t]);

  if (!isLogged) {
    return <AuthRequired />;
  }

  return (
    <>
      <Header showBackButton>{t('settings_auth_with_pass')}</Header>
      <ScrollView>
        <div className="Password">
          <Block>
            <Form>
              <div className="Password__intro">
                <div>{t('settings_auth_with_pass_link_intro')}</div>
              </div>
              <FormItem title={t('settings_auth_with_pass_link_label')}>
                <Input value={authLink} readOnly type="copy" />
              </FormItem>
            </Form>
          </Block>
          <Block>
            {hasPassword ? (
              <Form>
                <div className="Password__intro">
                  <div>{t('settings_auth_with_pass_password_reset_intro')}</div>
                </div>
                <FormItem>
                  <Button onClick={handleResetPassword}>
                    {t('settings_auth_with_pass_reset_pass')}
                  </Button>
                </FormItem>
              </Form>
            ) : (
              <Form>
                <div className="Password__intro">
                  <div>{t('settings_auth_with_pass_password_intro')}</div>
                </div>
                <FormItem title={t('settings_auth_with_pass_password_label')}>
                  <Input
                    type="password"
                    value={password}
                    onChange={handleChangePassword}
                  />
                </FormItem>
                <FormItem>
                  <Button onClick={handleSubmit}>
                    {t('settings_auth_with_pass_change_pass')}
                  </Button>
                </FormItem>
              </Form>
            )}
          </Block>
        </div>
      </ScrollView>
    </>
  );
};
